import React, { useState, useEffect } from 'react';

const TemplateConfigModal = ({ show, onClose }) => {
  const [config, setConfig] = useState({
    login_template_id: '',
    pep_template_id: '',
    age_template_id: '',
    access_token: ''
  });
  const [saved, setSaved] = useState(false);

  // Load config from localStorage on mount
  useEffect(() => {
    if (show) {
      const savedConfig = localStorage.getItem('templateConfig');
      if (savedConfig) {
        try {
          const parsed = JSON.parse(savedConfig);
          setConfig({
            login_template_id: parsed.login_template_id || '',
            pep_template_id: parsed.pep_template_id || '',
            age_template_id: parsed.age_template_id || '',
            access_token: parsed.access_token || ''
          });
        } catch (e) {
          console.error('Failed to parse template config:', e);
        }
      }
    }
  }, [show]);

  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
    setSaved(false);
  };

  const handleSave = () => {
    // Validate that all fields are filled
    if (!config.login_template_id || !config.pep_template_id || !config.age_template_id || !config.access_token) {
      alert('Please fill in all template IDs and access token');
      return;
    }

    // Validate that template IDs are numbers
    const loginId = parseInt(config.login_template_id);
    const pepId = parseInt(config.pep_template_id);
    const ageId = parseInt(config.age_template_id);

    if (isNaN(loginId) || isNaN(pepId) || isNaN(ageId)) {
      alert('Template IDs must be valid numbers');
      return;
    }

    // Validate access token is not empty
    if (!config.access_token.trim()) {
      alert('Access token is required');
      return;
    }

    // Save to localStorage
    const configToSave = {
      login_template_id: loginId,
      pep_template_id: pepId,
      age_template_id: ageId,
      access_token: config.access_token.trim()
    };

    localStorage.setItem('templateConfig', JSON.stringify(configToSave));
    setSaved(true);

    // Show success message briefly then close
    setTimeout(() => {
      onClose();
      setSaved(false);
    }, 1500);
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear the template configuration?')) {
      localStorage.removeItem('templateConfig');
      setConfig({
        login_template_id: '',
        pep_template_id: '',
        age_template_id: '',
        access_token: ''
      });
      setSaved(false);
    }
  };

  if (!show) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Configure Credential Templates</h2>
          <button onClick={onClose} style={styles.closeButton}>×</button>
        </div>

        <div style={styles.body}>
          <p style={styles.description}>
            Enter the template IDs from your Juno dashboard. These IDs determine which credential templates
            will be used for user registration and login.
          </p>

          <div style={styles.infoBox}>
            <strong>📋 Configuration Steps:</strong>
            <ol style={styles.infoList}>
              <li>Log in to your Juno dashboard</li>
              <li>Navigate to Credentials → Templates</li>
              <li>Find the template ID numbers for your LoginCredential, NoPEPHitCredential, and AgeCredential</li>
              <li>Navigate to Wallets section</li>
              <li>Copy the Access Token from your wallet</li>
              <li>Enter all values below</li>
            </ol>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Login Credential Template ID *
              <span style={styles.helpText}>Used for wallet-based authentication</span>
            </label>
            <input
              type="number"
              value={config.login_template_id}
              onChange={(e) => handleChange('login_template_id', e.target.value)}
              placeholder="Enter template ID"
              style={styles.input}
              min="1"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              PEP Credential Template ID *
              <span style={styles.helpText}>Used for PEP (Politically Exposed Person) verification</span>
            </label>
            <input
              type="number"
              value={config.pep_template_id}
              onChange={(e) => handleChange('pep_template_id', e.target.value)}
              placeholder="Enter template ID"
              style={styles.input}
              min="1"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Age Credential Template ID *
              <span style={styles.helpText}>Used for age verification (over 16/18/21)</span>
            </label>
            <input
              type="number"
              value={config.age_template_id}
              onChange={(e) => handleChange('age_template_id', e.target.value)}
              placeholder="Enter template ID"
              style={styles.input}
              min="1"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Wallet Access Token *
              <span style={styles.helpText}>Copy from your Juno wallet dashboard (determines which customer's templates to use)</span>
            </label>
            <input
              type="text"
              value={config.access_token}
              onChange={(e) => handleChange('access_token', e.target.value)}
              placeholder="Paste wallet access token here"
              style={styles.input}
            />
          </div>

          {saved && (
            <div style={styles.successMessage}>
              ✓ Configuration saved successfully!
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button onClick={handleClear} style={styles.clearButton}>
            Clear Config
          </button>
          <div style={styles.footerRight}>
            <button onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button onClick={handleSave} style={styles.saveButton}>
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  header: {
    padding: '20px 24px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#666',
    cursor: 'pointer',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '1'
  },
  body: {
    padding: '24px'
  },
  description: {
    color: '#666',
    marginBottom: '20px',
    lineHeight: '1.6'
  },
  infoBox: {
    backgroundColor: '#f0f7ff',
    border: '1px solid #b3d9ff',
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '24px'
  },
  infoList: {
    marginTop: '8px',
    marginBottom: '0',
    paddingLeft: '20px',
    lineHeight: '1.8'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    color: '#333',
    fontWeight: '500',
    fontSize: '14px'
  },
  helpText: {
    display: 'block',
    fontWeight: 'normal',
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
    fontStyle: 'italic'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  },
  successMessage: {
    backgroundColor: '#d4edda',
    color: '#155724',
    border: '1px solid #c3e6cb',
    borderRadius: '4px',
    padding: '12px',
    marginTop: '16px',
    textAlign: 'center'
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerRight: {
    display: 'flex',
    gap: '12px'
  },
  clearButton: {
    padding: '10px 20px',
    fontSize: '14px',
    border: '1px solid #dc3545',
    backgroundColor: 'white',
    color: '#dc3545',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    color: '#666',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  saveButton: {
    padding: '10px 24px',
    fontSize: '14px',
    border: 'none',
    backgroundColor: '#007bff',
    color: 'white',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.2s'
  }
};

export default TemplateConfigModal;
